import { Link, useLocation } from "wouter";
import { Award, Bookmark, ChevronDown, Heart, LayoutGrid, LogOut, Settings, ShieldCheck } from "lucide-react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/hooks/use-i18n";
import { useLocalizedUrl } from "@/hooks/use-localized-url";

// Voci del menu utente: identiche alle tendine "Esplora" e "Scopri" (14px, 40px, icona grigia 16px,
// voce della pagina corrente in azzurro tenue). Esci non è distruttivo: niente rosso.
const ITEM_CLASS = "rounded-md focus:bg-page focus:text-ink data-[highlighted]:bg-page data-[highlighted]:text-ink";
const itemLinkClass = (active: boolean) =>
  `flex min-h-[40px] w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-sm [&_svg]:h-4 [&_svg]:w-4 ${
    active ? "bg-surface-subtle font-medium text-action-hover [&_svg]:text-action-hover" : "font-normal [&_svg]:text-ink-muted"
  }`;

// Iniziali alla Chakra Avatar: prima lettera delle prime due parole di name.
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === "") return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Port 1:1 di user/auth desktop da ChakraHeader:499-595.
export function UserMenu() {
  const { t, language } = useI18n();
  const getLocalizedUrl = useLocalizedUrl();
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  const locationWithoutLang = location.replace(/^\/(it|en|de|sl)/, "") || "/";
  const isAuthPage = locationWithoutLang === "/auth";

  if (user) {
    const name = user.displayName || user.email;
    // Le stesse sezioni, nello stesso ordine, delle schede dell'area personale
    const areaItems = [
      { path: "/user/dashboard", label: t("profile.area.title"), icon: <LayoutGrid /> },
      { path: "/user/favorites", label: t("profile.area.favorites"), icon: <Heart /> },
      { path: "/user/bookmarks", label: t("profile.area.bookmarks"), icon: <Bookmark /> },
      { path: "/user/badges", label: t("profile.area.coins"), icon: <Award /> },
      { path: "/user/profile-page", label: t("profile.area.profile"), icon: <Settings /> },
    ];
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="hidden h-11 items-center gap-1 rounded-lg px-1.5 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 data-[state=open]:bg-white/10 md:flex"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.profileImageUrl || undefined} alt={name} />
              <AvatarFallback className="bg-white/90 text-[#C53030] text-sm font-medium">
                {getInitials(name)}
              </AvatarFallback>
            </Avatar>
            <ChevronDown className="h-[13px] w-[13px] text-white/80" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="z-[10000] min-w-[240px] rounded-lg border border-line-subtle bg-white p-1.5 text-ink shadow-overlay"
        >
          {/* Chi sei: nome ed email, come nella testata dell'area personale */}
          <DropdownMenuLabel className="px-3 pb-2 pt-1.5 font-normal">
            <span className="block truncate text-sm font-semibold text-ink">{name}</span>
            {user.displayName && <span className="block truncate text-xs text-ink-muted">{user.email}</span>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {areaItems.map(({ path, label, icon }) => (
            <DropdownMenuItem key={path} asChild className={ITEM_CLASS}>
              <Link href={`/${language}${path}`} className={itemLinkClass(locationWithoutLang === path)}>
                {icon}
                {label}
              </Link>
            </DropdownMenuItem>
          ))}

          {user.isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className={ITEM_CLASS}>
                <Link href="/admin" className={itemLinkClass(false)}>
                  <ShieldCheck />
                  {t("profile.area.admin")}
                </Link>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem className={`${ITEM_CLASS} ${itemLinkClass(false)}`} onSelect={() => logoutMutation.mutate()}>
            <LogOut />
            {t("auth.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (isAuthPage) return null;

  return (
    // hidden md:flex anche sull'<a>: su mobile un wrapper visibile a larghezza 0 è
    // comunque un flex item e consuma un gap — al login il nodo diventa il <button>
    // display:none dell'avatar e il cluster slittava di 8px a destra (layout shift).
    <Link
      href={getLocalizedUrl("/auth")}
      aria-label={t("auth.login")}
      className="hidden md:flex"
    >
      <span className="inline-flex h-11 items-center rounded-lg border border-white/50 px-[18px] text-sm font-medium text-white transition-colors hover:border-white/80 hover:bg-white/[0.12]">
        {t("auth.login")}
      </span>
    </Link>
  );
}
